"use client";

import { Pause, Play } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { Button } from "@/components/ui/button";

// One recording at a time — starting one pauses whichever was playing.
let nowPlaying: HTMLAudioElement | null = null;

export function RecordingButton({ src, transcript }: { src: string; transcript: string }) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    return () => audioRef.current?.pause();
  }, []);

  function toggle() {
    if (!audioRef.current) {
      // Created on first click, so audio bytes are fetched only when asked for.
      const audio = new Audio(src);
      audio.onplay = () => {
        if (nowPlaying && nowPlaying !== audio) nowPlaying.pause();
        nowPlaying = audio;
        setPlaying(true);
      };
      audio.onpause = () => setPlaying(false);
      audio.onended = () => {
        audio.currentTime = 0;
        setPlaying(false);
      };
      // A failed load fires `play` first and never `pause`, which would leave
      // the pause icon stuck — reset here, and drop the element so the next
      // click retries with a fresh fetch.
      audio.onerror = () => {
        setPlaying(false);
        audioRef.current = null;
        if (nowPlaying === audio) nowPlaying = null;
      };
      audioRef.current = audio;
    }
    if (playing) {
      audioRef.current.pause();
    } else {
      // Load failures surface through onerror above; an autoplay-policy
      // rejection never fires `play`, so the icon stays consistent either way.
      void audioRef.current.play().catch(() => {});
    }
  }

  // The transcript disambiguates this recording from every other play button
  // on the page for screen-reader users.
  const clip = transcript.length > 60 ? `${transcript.slice(0, 60)}…` : transcript;

  return (
    <Button
      variant="ghost"
      size="icon-xs"
      className="rounded-full"
      onClick={toggle}
      aria-label={playing ? "Pause recording" : `Play recording: ${clip}`}
    >
      {playing ? <Pause /> : <Play />}
    </Button>
  );
}
